import { apiFetch, ApiError } from "./client";

/** Land Radar (zaključani modul) — klijent za /api/radar/*. */

export interface RadarParsed {
  opcina: string | null;
  macro: string | null;
  ha_min: number | null;
  ha_max: number | null;
  coastal: boolean | null;
  klasa: string | null;
  bend: string | null;
}

export interface RadarResult {
  id: number;
  zone_key: string;
  opcina: string;
  naselje: string | null;
  naziv: string | null;
  oznaka: string | null;
  macro: string | null;
  detaljna_namjena: string | null;
  ha: number | null;
  dist_sea_m: number | null;
  coastal: boolean | null;
  klasa: string | null;
  plan_naziv: string | null;
  plan_donesen: string | null;
  evidence_tier: string;
  val_eur_m2: number | null;
  trzisna_mil: number | null;
  bend: string | null;
  n_parcels: number | null;
  n_clusters: number | null;
  top_cluster_pct: number | null;
  net_ha: number | null;         // neto gradivo (clip čestica×zona) — pari s acq_zona_ha, NE s ha
  acq_zona_ha: number | null;    // površina zone na koju je net_ha clipan (union oznaka% poligona); net_ha ≤ acq_zona_ha
  n_blocks: number | null;
  max_block_pct: number | null;
}

export interface RadarQueryResponse {
  parsed: RadarParsed;
  count: number;
  results: RadarResult[];
  caveats: string[];
}

export interface RadarBrownfield {
  naziv: string;
  ha: number | null;
  planirana_namjena: string | null;
  planska_oznaka: string | null;
  financijski_rizik: string | null;
  status_regeneracije: string | null;
}

/** GeoJSON geometrija zone (EPSG:4326, iz LR-3 backfilla) — za Leaflet kartu. */
export interface RadarGeometry {
  type: "Polygon" | "MultiPolygon";
  coordinates: number[][][] | number[][][][];
}

export interface RadarZoneDetail {
  zone: RadarResult & {
    ko: string[] | null;
    kc_primjeri: string[] | null;
    max_block_ha: number | null;
    median_parcel_ha: number | null;
    verdikt_napomena: string;
    geometry?: RadarGeometry | null;
  };
  brownfield_u_opcini: RadarBrownfield[];
  caveats: string[];
}

async function errFrom(res: Response): Promise<never> {
  let detail = "";
  try {
    detail = ((await res.json()) as { detail?: string })?.detail ?? "";
  } catch {
    // not JSON
  }
  throw new ApiError(res.status, detail || res.statusText);
}

/** LR-6: mapiranja za Permitko handoff — backend je izvor istine, lokalni
 *  fallback u RadarPage čuva offline UX ako fetch padne. */
export interface RadarMeta {
  macro_type: Record<string, string>;
  t_subtype: Record<string, string>;
}

export async function fetchRadarMeta(): Promise<RadarMeta> {
  const res = await apiFetch("/api/radar/meta");
  if (!res.ok) return errFrom(res);
  return (await res.json()) as RadarMeta;
}

export async function radarQuery(text: string, limit = 20): Promise<RadarQueryResponse> {
  const res = await apiFetch("/api/radar/query", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, limit }),
  });
  if (!res.ok) return errFrom(res);
  return (await res.json()) as RadarQueryResponse;
}

export async function fetchRadarZone(id: number): Promise<RadarZoneDetail> {
  const res = await apiFetch(`/api/radar/zone/${id}`);
  if (!res.ok) return errFrom(res);
  return (await res.json()) as RadarZoneDetail;
}

export interface RadarVerdiktLimit {
  label: string;
  op: string;
  value: number;
  unit: string;
  article: string | null;
}

export interface RadarVerdiktBlocker {
  parameter: string;
  project_value: number | null;
  limit_value: number | null;
  source_article: string | number | null;
}

export interface RadarVerdikt {
  opcina: string;
  oznaka: string;
  municipality_code: string;
  plan_version: string;
  subtype_matched: boolean;
  rules_used: string[];
  limits: RadarVerdiktLimit[];
  /** NEMA_PRAVILA (RB-2): općina učitana, ali motor nema nijedno pravilo —
   *  ručna provjera; NIKAD se ne prikazuje kao zeleno/DOPUŠTENO. */
  outcome: "DOPUŠTENO" | "BLOKADA" | "NEMA_PRAVILA" | null;
  /** Popunjeno samo uz outcome NEMA_PRAVILA. */
  poruka?: string;
  blockers: RadarVerdiktBlocker[];
  caveats: string[];
}

export interface RadarVerdiktParams {
  kig?: number;
  kisn?: number;
  et?: number;
  vis?: number;
}

/** Deterministički verdikt (bez LLM-a) — get_evidence_bundle + numeric_checker. */
export async function radarVerdikt(
  zoneId: number,
  params: RadarVerdiktParams,
): Promise<RadarVerdikt> {
  const res = await apiFetch(`/api/radar/zone/${zoneId}/verdikt`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) return errFrom(res);
  return (await res.json()) as RadarVerdikt;
}
